import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import Ctas from '~/components/atoms/Ctas/Ctas'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import NumberedCircle from '~/components/atoms/NumberedCircle/NumberedCircle'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import type { Block } from '~/services/sanity'

import styles from './HowItWorksHero.module.css'

interface HowItWorksHeroProps {
  title: string
  summary: Block[]
  image: ImageProps
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  noPaddingTop?: boolean
  flip?: boolean
  testID?: string
  ctas?: any
  moduleId?: string
  fullWidth?: boolean
  numberedListTitle?: string
  /** CMS toggle: renders the summary body copy at the larger body size */
  largeSummary?: boolean
  footerNote?: string
}

export default function HowItWorksHero({
  title,
  summary,
  image,
  theme = ComponentTheme.primary,
  verticalPadding = true,
  noPaddingBottom,
  noPaddingTop = false,
  testID,
  moduleId,
  flip = false,
  fullWidth = false,
  ctas,
  numberedListTitle,
  largeSummary = false,
  footerNote,
}: HowItWorksHeroProps) {
  const content = (
    <div
      className={classNames(styles.base, {
        [styles.flip]: flip,
      })}
    >
      {/* Image */}
      <div className={styles.imageContainer}>
        <Image
          asset={image.asset}
          objectFit="cover"
          sizes="(max-width: 799px) 100vw,
            50vw"
        />
        {image?.credit?.text && (
          <Text
            className={classNames(styles.imageCredit, {
              [styles[image.credit.theme]]: image.credit.theme,
            })}
            size={TextSizes.tertiaryTitleSml}
          >
            {image.credit.text}
          </Text>
        )}
      </div>
      {/* Content */}
      <div
        className={classNames(styles.contentContainer, {
          [styles.fullWidth]: fullWidth,
          [styles.hasNumberedListTitle]: !!numberedListTitle,
        })}
      >
        {numberedListTitle && (
          <NumberedCircle
            number={numberedListTitle}
            className={styles.numberedCircle}
          />
        )}
        <Text className={styles.title} size={TextSizes.primaryTitleLrg}>
          <div dangerouslySetInnerHTML={{ __html: title }}></div>
        </Text>
        <div className={classNames(styles.summary, 'prose')}>
          <BlockContent
            value={summary}
            size={largeSummary ? TextSizes.bodyLrg : undefined}
          ></BlockContent>
        </div>
        <div className={styles.ctas}>
          <Ctas ctas={ctas} />
        </div>
        {footerNote && (
          <Text className={styles.footerNote} size={TextSizes.bodySml}>
            {footerNote}
          </Text>
        )}
      </div>
    </div>
  )

  return (
    <Fixture
      verticalPadding={verticalPadding}
      theme={theme}
      testID={testID}
      noPaddingBottom={noPaddingBottom}
      noPaddingTop={noPaddingTop}
      id={moduleId}
      className={classNames({
        [styles.minLargePadding]: !!numberedListTitle,
        [styles.noPaddingTop]: noPaddingTop,
      })}
    >
      {fullWidth ? content : <Container>{content}</Container>}
    </Fixture>
  )
}
